import {
  CHAT_PROTOCOL_VERSION,
  type ChatAttachment,
  type ServerFrame,
  type SupportAgentCtx,
  type WidgetStartResponse,
} from './types.js';

type Listener = (frame: ServerFrame) => void;

/**
 * The session lives in THIS browser: the server never hands an unverified
 * visitor an existing conversation (that would let anyone who types another
 * person's email read their chat). So the widget keeps its own token across
 * page loads — the customer can move from product page to cart and keep the
 * conversation, and nobody else can get at it.
 */
interface StoredSession {
  token: string;
  threadId: string;
  expiresAt: string;
  identity: string;
}

function storageKey(shop: string): string {
  return `sa-chat-session:${shop}`;
}

function loadSession(shop: string, identity: string): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(storageKey(shop));
    if (!raw) return null;
    const s = JSON.parse(raw) as StoredSession;
    if (s.identity !== identity) return null;
    if (!s.token || Date.parse(s.expiresAt) - Date.now() < 60_000) return null;
    return s;
  } catch {
    return null;
  }
}

/**
 * The email a guest typed into the chat earlier in this browser (their open
 * session's identity) — so moving to the next page doesn't ask again.
 */
export function storedGuestEmail(shop: string): string | null {
  try {
    const raw = window.localStorage.getItem(storageKey(shop));
    if (!raw) return null;
    const s = JSON.parse(raw) as StoredSession;
    if (!s.token || Date.parse(s.expiresAt) - Date.now() < 60_000) return null;
    return s.identity.includes('@') && !s.identity.startsWith('customer:') ? s.identity : null;
  } catch {
    return null;
  }
}

/** The server renewed the session on connect: remember its new end. */
function renewSession(shop: string, token: string, expiresAt: string): void {
  try {
    const raw = window.localStorage.getItem(storageKey(shop));
    if (!raw) return;
    const s = JSON.parse(raw) as StoredSession;
    if (s.token !== token) return;
    window.localStorage.setItem(storageKey(shop), JSON.stringify({ ...s, expiresAt }));
  } catch {
    /* storage unavailable — the session still lasts this page */
  }
}

function saveSession(shop: string, s: StoredSession): void {
  try {
    window.localStorage.setItem(storageKey(shop), JSON.stringify(s));
  } catch { /* storage blocked — the session just won't survive a reload */ }
}

function clearSession(shop: string): void {
  try {
    window.localStorage.removeItem(storageKey(shop));
  } catch { /* ignore */ }
}

interface PendingOutbound {
  externalMessageId: string;
  body: string;
  page: { url: string; title: string };
}

/**
 * Tiny WS client for the storefront widget.
 *  - Calls POST /chat/widget/start once it has an identifier.
 *  - Keeps the WS open with exponential-backoff reconnect (capped at 30s).
 *  - Keeps every message until the server's receipt (the echo carrying its
 *    externalMessageId) and sends what is still unconfirmed after each
 *    `welcome` — a message typed while the socket was down, or sent into a
 *    socket that died before the receipt, is not lost. Server idempotency is
 *    keyed by externalMessageId so replays are safe.
 */
export class ChatClient {
  private apiOrigin: string;
  private ctx: SupportAgentCtx;
  private listeners = new Set<Listener>();

  private ws: WebSocket | null = null;
  private token: string | null = null;
  private threadId: string | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  /** Messages without a receipt yet, oldest first. */
  private outbox: PendingOutbound[] = [];
  /** The server has welcomed the current socket — `hello` is through. */
  private ready = false;
  /** Outbox messages already sent on the current socket. */
  private sentHere = new Set<string>();
  private destroyed = false;
  /** The current token came from storage; if the server rejects it, start fresh once. */
  private resumed = false;
  /** The email the session was started with (reused by restart()). */
  private email: string | undefined;

  constructor(ctx: SupportAgentCtx) {
    this.ctx = ctx;
    this.apiOrigin = ctx.apiOrigin ?? inferApiOrigin();
  }

  on(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  destroy() {
    this.destroyed = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close(1000, 'destroyed');
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  /** Start (or restart) the session, optionally upgrading the email. */
  async start(emailOverride?: string): Promise<void> {
    if (this.destroyed) return;
    const email = emailOverride ?? this.ctx.customerEmail ?? undefined;
    const customerId = this.ctx.customerId ?? undefined;
    if (!email && !customerId) {
      throw new Error('email_required');
    }
    this.email = email;
    const identity = (email ?? `customer:${customerId}`).toLowerCase();
    const stored = loadSession(this.ctx.shop, identity);
    if (stored) {
      this.token = stored.token;
      this.threadId = stored.threadId;
      this.resumed = true;
      this.openSocket();
      return;
    }
    const body = {
      shopDomain: this.ctx.shop,
      shopifyCustomerId: customerId,
      email,
      customerName: this.ctx.customerName ?? undefined,
    };
    // When a proxyPath is configured, hit the handshake same-origin through the
    // Shopify App Proxy so Shopify signs a verified logged-in customer id.
    // Otherwise call the API directly (guarded by the shop-domain allowlist).
    const startUrl = this.ctx.proxyPath
      ? `${window.location.origin}${this.ctx.proxyPath}/widget/start`
      : `${this.apiOrigin}/chat/widget/start`;
    const res = await fetch(startUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(`start_failed_${res.status}`);
    }
    const data: WidgetStartResponse = await res.json();
    this.token = data.token;
    this.threadId = data.threadId;
    this.resumed = false;
    saveSession(this.ctx.shop, { token: data.token, threadId: data.threadId, expiresAt: data.expiresAt, identity });
    this.openSocket();
  }

  /**
   * Leave this conversation and start a new one with the same email: the
   * stored session is forgotten, so the server opens a fresh chat. The old
   * conversation stays in the CRM like any chat the customer walked away from.
   */
  async restart(): Promise<void> {
    if (this.destroyed) return;
    clearSession(this.ctx.shop);
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const old = this.ws;
    this.ws = null;
    try {
      old?.close(1000, 'new conversation');
    } catch {
      /* ignore */
    }
    this.token = null;
    this.threadId = null;
    this.outbox = [];
    this.ready = false;
    this.sentHere.clear();
    this.reconnectAttempt = 0;
    this.resumed = false;
    await this.start(this.email);
  }

  /** Send a message — buffers locally and reconnects if the WS is down. */
  send(body: string): string {
    const externalMessageId = uuidv4();
    // Where the customer is when they ask — "har dere den i blå?" on a
    // product page means that product.
    const page = { url: window.location.href.slice(0, 1000), title: document.title.slice(0, 300) };
    this.outbox.push({ externalMessageId, body, page });
    this.flushOutbox();
    return externalMessageId;
  }

  /**
   * A picture as its own message (HTTP, not the socket). The echo on the
   * socket confirms it by `externalMessageId`; so does the response, for
   * when the socket is down.
   */
  sendPicture(picture: Blob, filename: string): { externalMessageId: string; upload: Promise<PictureSent> } {
    const externalMessageId = uuidv4();
    const upload = (async (): Promise<PictureSent> => {
      if (!this.token) throw new Error('no_session');
      let res: Response;
      try {
        res = await fetch(`${this.apiOrigin}/chat/widget/picture?id=${externalMessageId}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Content-Type': picture.type || 'image/jpeg',
            'X-Filename': encodeURIComponent(filename),
          },
          body: picture,
        });
      } catch {
        throw new Error('network');
      }
      const json = (await res.json().catch(() => ({}))) as Partial<PictureSent> & { error?: string };
      if (!res.ok || !json.messageId) throw new Error(json.error ?? (res.status === 413 ? 'too_large' : 'upload_failed'));
      return { messageId: json.messageId, attachments: json.attachments ?? [] };
    })();
    return { externalMessageId, upload };
  }

  /** Email a verification code (to `email`, or the address the chat started with). */
  async verifyStart(email?: string): Promise<VerifyStartResult> {
    if (!this.token) return { ok: false, error: 'no_session' };
    return postJson(`${this.apiOrigin}/chat/widget/verify/start`, { token: this.token, ...(email ? { email } : {}) });
  }

  /** Check the code the customer typed. */
  async verifyCheck(code: string): Promise<VerifyCheckResult> {
    if (!this.token) return { ok: false, error: 'no_session' };
    return postJson(`${this.apiOrigin}/chat/widget/verify/check`, { token: this.token, code });
  }

  /** "Snakk med en ansatt": ask for a person. */
  requestHandoff() {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify({ v: CHAT_PROTOCOL_VERSION, type: 'handoff' }));
    return true;
  }

  setTyping(isTyping: boolean) {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(
      JSON.stringify({
        v: CHAT_PROTOCOL_VERSION,
        type: 'typing',
        isTyping,
      }),
    );
  }

  // ─── Internals ───────────────────────────────────────────────────────────

  private openSocket() {
    if (!this.token) return;
    const wsOrigin = this.apiOrigin.replace(/^http/, 'ws');
    const url = `${wsOrigin}/chat/widget`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener('open', () => {
      if (this.ws !== ws) return; // replaced (new conversation) — ignore
      this.reconnectAttempt = 0;
      this.ready = false;
      this.sentHere.clear();
      ws.send(
        JSON.stringify({
          v: CHAT_PROTOCOL_VERSION,
          type: 'hello',
          token: this.token,
        }),
      );
      // The outbox goes after `welcome`, once `hello` is through.
    });

    ws.addEventListener('message', (e) => {
      if (this.ws !== ws) return; // a frame from a conversation we left
      let frame: ServerFrame;
      try {
        frame = JSON.parse(String(e.data)) as ServerFrame;
      } catch {
        return; // ignore malformed
      }
      if (frame.type === 'welcome') {
        if (frame.sessionExpiresAt && this.token) renewSession(this.ctx.shop, this.token, frame.sessionExpiresAt);
        // Stored already (its receipt went down with the old socket).
        const stored = new Set(frame.history.map((m) => m.externalMessageId).filter(Boolean));
        this.outbox = this.outbox.filter((m) => !stored.has(m.externalMessageId));
        this.ready = true;
        this.flushOutbox();
      } else if (frame.type === 'message' && frame.externalMessageId) {
        const id = frame.externalMessageId;
        this.outbox = this.outbox.filter((m) => m.externalMessageId !== id);
      }
      for (const l of this.listeners) l(frame);
    });

    ws.addEventListener('close', (e) => {
      if (this.ws !== ws) return; // an old socket closing after a restart
      this.ws = null;
      this.ready = false;
      // 4401/4403 → token bad/expired; need to call start() again.
      if (e.code === 4401 || e.code === 4403) {
        this.token = null;
        clearSession(this.ctx.shop);
        if (this.resumed) {
          // A stored session the server no longer accepts (expired, revoked):
          // quietly start a new one instead of showing an error.
          this.resumed = false;
          void this.start().catch(() => { /* surfaced by the UI's own start() handling */ });
          return;
        }
        // The UI listens for type:'error' but a close before any frame is
        // silent — surface it as a synthetic frame.
        for (const l of this.listeners) {
          l({
            v: CHAT_PROTOCOL_VERSION,
            type: 'error',
            code: 'session_invalid',
            message: 'Session expired — please reconnect.',
          });
        }
        return;
      }
      this.scheduleReconnect();
    });

    ws.addEventListener('error', () => {
      // close handler will run; nothing to do here.
    });
  }

  /** Send what this socket hasn't sent yet. Each message stays in the
   *  outbox until its receipt, and is sent again on the next socket. */
  private flushOutbox() {
    if (!this.ready || this.ws?.readyState !== WebSocket.OPEN) return;
    for (const msg of this.outbox) {
      if (this.sentHere.has(msg.externalMessageId)) continue;
      try {
        this.ws.send(
          JSON.stringify({
            v: CHAT_PROTOCOL_VERSION,
            type: 'message',
            externalMessageId: msg.externalMessageId,
            body: msg.body,
            page: msg.page,
          }),
        );
        this.sentHere.add(msg.externalMessageId);
      } catch {
        return; // try again on reconnect
      }
    }
  }

  private scheduleReconnect() {
    if (this.destroyed) return;
    if (!this.token) return;
    const delay = Math.min(30_000, 500 * Math.pow(2, this.reconnectAttempt++));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay) as unknown as number;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export interface PictureSent {
  messageId: string;
  attachments: ChatAttachment[];
}

export type VerifyStartResult =
  | { ok: true; sentTo: string; testMode: boolean }
  | { ok: false; error: string };
export type VerifyCheckResult =
  | { ok: true; email: string }
  | { ok: false; error: string; attemptsLeft?: number };

async function postJson<T extends { ok: boolean }>(url: string, body: object): Promise<T> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) return { ok: false, error: String(data.error ?? `http_${res.status}`), ...data } as unknown as T;
    return { ok: true, ...data } as unknown as T;
  } catch {
    return { ok: false, error: 'network' } as unknown as T;
  }
}

/**
 * Is anyone in sales online, and does this shop's chat start with the AI
 * assistant? `hours` = the chat's hours, only while it is closed (evening,
 * Sunday, public holiday). null when the API can't be reached.
 */
export async function fetchChatStatus(ctx: SupportAgentCtx): Promise<{ online: boolean; ai: boolean; hours: string | null } | null> {
  try {
    const url = `${ctx.apiOrigin ?? inferApiOrigin()}/chat/widget/status?shop=${encodeURIComponent(ctx.shop)}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    const d = (await res.json()) as { online?: boolean; ai?: boolean; closed?: boolean; hours?: string };
    return { online: d.online === true, ai: d.ai === true, hours: d.closed && typeof d.hours === 'string' ? d.hours : null };
  } catch {
    return null;
  }
}

export function inferApiOrigin(): string {
  // Look for our own <script src="…/widget.js"> tag and use its origin.
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'));
  for (const s of scripts) {
    if (s.src.endsWith('/widget.js') || s.src.includes('/widget.js?')) {
      try {
        return new URL(s.src).origin;
      } catch {
        /* ignore */
      }
    }
  }
  // Fallback to current origin (dev mode where widget is served from same host).
  return window.location.origin;
}

function uuidv4(): string {
  // Prefer crypto.randomUUID; fall back to a quick polyfill for older browsers.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
